import { useElementSize, useMediaQuery } from "@mantine/hooks";
import { useI18n } from "@wojo/localization";
import {
    Button,
    Container,
    Modal,
    ModalBody,
    ModalFooter,
    Text,
} from "@wojo/ui";
import cn from "clsx";
import { useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import { useTourPageContext } from "../../_client/context";
import styles from "./EquipmentModal.module.scss";
import { EquipmentModalInfo } from "./EquipmentModalInfo";
import { EquipmentModalMedia } from "./EquipmentModalMedia";
import { EquipmentModalNav } from "./EquipmentModalNav";

type EquipmentModalProps = {
    selectedIndex?: number;
    open?: boolean;
    onCloseRequested: () => void;
};

export const EquipmentModal: React.FC<EquipmentModalProps> = ({
    open = false,
    selectedIndex = 0,
    onCloseRequested,
}) => {
    const [activeIndex, setActiveIndex] = useState(selectedIndex);
    const { productPage } = useI18n("productPage");
    const prefersReducedMotion = useMediaQuery(
        "(prefers-reduced-motion: reduce)",
    );
    const { ref: headerRef, height } = useElementSize();
    const mediaRef = useRef<HTMLDivElement>(null);
    const breakpointMobile = "(min-width: 992px)";
    const isDesktop = useMediaQuery(breakpointMobile, false);

    const { tourContent } = useTourPageContext();
    const equipment = tourContent.equipment;
    if (!equipment?.length) {
        return null;
    }

    const tourEquipmentLength = equipment.length;

    const scrollToTop = () => {
        const scrollToElement = isDesktop
            ? mediaRef.current
            : headerRef.current?.parentElement;
        scrollToElement?.scrollTo({
            top: 0,
            behavior: prefersReducedMotion ? "instant" : "smooth",
        });
    };

    const handlePrevClick = () => {
        setActiveIndex(
            (prevIndex) =>
                (prevIndex - 1 + tourEquipmentLength) % tourEquipmentLength,
        );
        scrollToTop();
    };

    const handleNextClick = () => {
        setActiveIndex((prevIndex) => (prevIndex + 1) % tourEquipmentLength);
        scrollToTop();
    };

    return (
        <Modal
            hideDefaultHeader
            isOpen={open}
            onCloseRequested={onCloseRequested}
            accessibleTitle={productPage.equipment.title.l()}
            variant="fullscreen"
            contentClassName={styles.modalDialog}
            style={
                {
                    "--modal-header-height": !!height ? `${height}px` : "64px",
                } as React.CSSProperties
            }
        >
            <header
                ref={headerRef}
                style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                }}
            >
                <Text.Body emphasis="1" tag="div" className={styles.header}>
                    <Button
                        variant="quiet"
                        adornRight={<FiX aria-hidden size={20} />}
                        onClick={onCloseRequested}
                        className={styles.close}
                        type="button"
                    >
                        {productPage.equipment.modalHeaderClose.l()}
                    </Button>
                </Text.Body>
            </header>
            <Container>
                <ModalBody className={styles["modal-body-wrapper"]}>
                    <div className={styles["info-content"]}>
                        {tourEquipmentLength > 1 && (
                            <EquipmentModalNav
                                handleNextClick={handleNextClick}
                                handlePrevClick={handlePrevClick}
                                className={styles["desktop-only"]}
                                tourEquipmentTitle={
                                    equipment[activeIndex]?.name ?? ""
                                }
                            />
                        )}
                        <EquipmentModalInfo
                            tourEquipment={equipment[activeIndex]}
                        />
                    </div>
                    <EquipmentModalMedia
                        mediaAssets={equipment[activeIndex].assets}
                        ref={mediaRef}
                    />
                </ModalBody>
            </Container>
            {tourEquipmentLength > 1 && (
                <ModalFooter
                    className={cn(styles.footer, styles["mobile-only"])}
                >
                    <EquipmentModalNav
                        handleNextClick={handleNextClick}
                        handlePrevClick={handlePrevClick}
                        className={styles["mobile-only"]}
                        tourEquipmentTitle={equipment[activeIndex]?.name ?? ""}
                    />
                </ModalFooter>
            )}
        </Modal>
    );
};
